import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Send, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';
const DEFAULT_MESSAGE = 'Please take a look at the below, can you please let me know the actual contribution / Real contribution on the below.';
const money = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "Send for review" — pick a recipient via a live search-as-you-type box (searches
 * all approved users, not just the current branch — the old branch-only dropdown
 * silently excluded super_admin whenever their branch didn't match), plus a separate
 * free-text "or enter a custom email" field. */
export default function OrderBookSendReviewModal({ isOpen, onClose, branchId, selectedOrders, onSent }) {
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // { email, full_name }
  const [customEmail, setCustomEmail] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setMessage(DEFAULT_MESSAGE);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setCustomEmail('');
  }, [isOpen, branchId]);

  // Debounced live search across ALL users
  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      api.get(`${API}/order-book-reports/branch-users`, { params: { branch_id: branchId, search: searchQuery.trim() } })
        .then((res) => setSearchResults(res.data?.users || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, isOpen, branchId]);

  const effectiveRecipient = customEmail.trim() || selectedUser?.email || '';

  const handleSend = async () => {
    if (!effectiveRecipient) { toast.error('Select a user or enter an email to send this to'); return; }
    setSending(true);
    try {
      await api.post(`${API}/order-book-reports/review-requests`, {
        recipient_email: effectiveRecipient,
        message,
        branch_id: branchId,
        lines: selectedOrders.map(o => ({
          order_number: o.order_number,
          customer_name: o.customer_name || '',
          bm_number: o.bm_number || '',
          delivery_date: o.delivery_date || '',
          sales_order_value: o.sales_order_value || 0,
          contribution_value: o.contribution_value || 0,
          contribution_pct: o.contribution_pct || 0,
        })),
      });
      toast.success(`Sent to ${effectiveRecipient}`);
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send review request');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="send-review-modal">
        <DialogHeader>
          <DialogTitle>Send Order Book lines for review</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Send to</label>

            <div className="space-y-2">
              {selectedUser ? (
                <div className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 text-sm bg-orange-50" data-testid="send-review-selected-user">
                  <span className="text-gray-800 font-medium">{selectedUser.full_name} <span className="text-gray-400">({selectedUser.email})</span></span>
                  <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search any user by name or email…"
                    data-testid="send-review-user-search-input"
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': enconOrange }}
                  />
                  {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-300" />}
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="send-review-user-search-results">
                      {searchResults.map(u => (
                        <button
                          key={u.id}
                          onClick={() => { setSelectedUser(u); setSearchQuery(''); setSearchResults([]); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 border-b border-gray-50 last:border-b-0"
                          data-testid={`send-review-user-option-${u.id}`}
                        >
                          <span className="font-medium text-gray-800">{u.full_name}</span>{' '}
                          <span className="text-gray-400">({u.email})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-[11px] text-gray-400 uppercase font-semibold">or</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              <input
                type="email"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="Enter a custom email address…"
                data-testid="send-review-custom-email-input"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': enconOrange }}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              data-testid="send-review-message-textarea"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 resize-none"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Selected lines ({selectedOrders.length})
            </label>
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
              {selectedOrders.map(o => (
                <div key={o.order_number} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`send-review-line-${o.order_number}`}>
                  <span className="text-gray-700 font-medium truncate">{o.customer_name} <span className="text-gray-400 font-mono">#{o.order_number}</span></span>
                  <span className="text-gray-500 whitespace-nowrap ml-2">{money(o.contribution_value)} ({(o.contribution_pct || 0).toFixed(2)}%)</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="send-review-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={sending || !effectiveRecipient}
              className="text-white"
              style={{ backgroundColor: enconOrange }}
              data-testid="send-review-confirm-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
